import React, { useRef, useState } from "react";
import { Play, Pause, Volume2, Loader2 } from "lucide-react";
import { api } from "../lib/data";

export function MediaPlayer({ kind, itemId, image, caption, name, role, label }) {
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const toggle = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    if (!loaded) {
      setLoading(true);
      try {
        const { data } = await api.get(`/media/audio/${kind}/${itemId}`);
        audioRef.current.src = `data:audio/mp3;base64,${data.audio_base64}`;
        setLoaded(true);
      } catch {
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    await audioRef.current.play();
    setPlaying(true);
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid={`media-player-${itemId}`}>
      <div className="relative aspect-video overflow-hidden bg-[#0A1914]">
        <img src={image} alt={name || label} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1914]/80 via-transparent to-transparent" />
        {playing && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-[#00B368] text-white text-xs font-mono px-2 py-1">
            <span className="relative flex h-2 w-2">
              <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            LIVE
          </div>
        )}
        <button
          onClick={toggle}
          data-testid={`play-btn-${itemId}`}
          className="absolute inset-0 m-auto h-16 w-16 flex items-center justify-center bg-white/95 hover:bg-[#00B368] hover:text-white transition-colors duration-300 rounded-full"
          aria-label="Play"
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
        </button>
        {(name || label) && (
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <p className="font-display text-lg leading-tight">{name || label}</p>
            {role && <p className="text-xs text-white/70 font-mono uppercase tracking-wider">{role}</p>}
          </div>
        )}
      </div>
      {caption && (
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-[#00B368] font-mono uppercase tracking-wider mb-2">
            <Volume2 className="h-3.5 w-3.5" /> {playing ? "Now speaking" : "Captions"}
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">&ldquo;{caption}&rdquo;</p>
        </div>
      )}
      <audio ref={audioRef} onEnded={() => setPlaying(false)} className="hidden" />
    </div>
  );
}
